import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Typography } from 'antd';
import { enzymeLibrary } from '../../../data/enzymeLibrary';
import { normalizeSequenceInput } from '../../../services/enzymeAnalysis.service';

const { Text } = Typography;

interface CutSite {
  enzymeName: string;
  cutPosition: number;
  recognitionSite: string;
}

interface CutMapProps {
  sequence: string;
  cutSites: CutSite[];
}

const CutMap: React.FC<CutMapProps> = ({ sequence, cutSites }) => {
  const cleanSequence = normalizeSequenceInput(sequence);
  const seqLength = cleanSequence.length;
  
  // 状态管理
  const [hoveredEnzyme, setHoveredEnzyme] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // 用于测量文本宽度的离屏 canvas（缓存）
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [selectedEnzyme, setSelectedEnzyme] = useState<string | null>(null);
  const [clickCount, setClickCount] = useState<Record<string, number>>({});
  const [highlightedSite, setHighlightedSite] = useState<CutSite | null>(null);
  const [selectedEnzymeForHighlight, setSelectedEnzymeForHighlight] = useState<string | null>(null);
  const [clusterClickCount, setClusterClickCount] = useState<Record<string, number>>({});

  // SVG视图控制：用于缩放和平移
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, width: 700, height: 750 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });

  // 处理鼠标滚轮缩放
  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;

    const zoomFactor = 1.1;
    const { deltaY } = e;
    const { x, y, width, height } = viewBox;

    const point = new DOMPoint(e.clientX, e.clientY);
    const svgPoint = point.matrixTransform(svg.getScreenCTM()?.inverse());

    const newWidth = deltaY < 0 ? width / zoomFactor : width * zoomFactor;
    const newHeight = deltaY < 0 ? height / zoomFactor : height * zoomFactor;

    const newX = svgPoint.x - (svgPoint.x - x) * (newWidth / width);
    const newY = svgPoint.y - (svgPoint.y - y) * (newHeight / height);

    setViewBox({ x: newX, y: newY, width: newWidth, height: newHeight });
  };

  // 处理平移开始
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    setIsPanning(true);
    setStartPoint({ x: e.clientX, y: e.clientY });
  };

  // 处理平移过程
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isPanning) return;
    e.preventDefault();

    const dx = e.clientX - startPoint.x;
    const dy = e.clientY - startPoint.y;

    // 缩放比例，用于调整平移速度
    const scale = viewBox.width / 700;

    setViewBox(prev => ({ ...prev, x: prev.x - dx * scale, y: prev.y - dy * scale }));
    setStartPoint({ x: e.clientX, y: e.clientY });
  };

  // 处理平移结束
  const handleMouseUp = () => {
    setIsPanning(false);
  };

  
  if (seqLength === 0) {
    return null;
  }

  if (cutSites.length === 0) {
    return <Text type="secondary">未找到酶切位点。如果您预期有切点，请尝试选择"所有酶"选项。</Text>;
  }

  // 按酶的名称分组切割位点
  const enzymeGroups = useMemo(() => {
    const groups: Record<string, CutSite[]> = {};
    cutSites.forEach(site => {
      if (!groups[site.enzymeName]) {
        groups[site.enzymeName] = [];
      }
      groups[site.enzymeName].push(site);
    });
    return groups;
  }, [cutSites]);

  // 为每个酶分配一个唯一的颜色
  const colorMap = useMemo(() => {
    const colors = [
      '#D32F2F', '#7B1FA2', '#1976D2', '#388E3C', '#F57C00', 
      '#5D4037', '#455A64', '#00796B', '#C2185B', '#303F9F',
      '#689F38', '#0097A7', '#6D4C41', '#512DA8', '#616161'
    ];
    const map: Record<string, string> = {};
    
    Object.keys(enzymeGroups).forEach((enzyme, index) => {
      map[enzyme] = colors[index % colors.length];
    });
    
    return map;
  }, [enzymeGroups]);

  // 从enzymeLibrary获取酶的切割位置信息
  const getEnzymeCutPosition = (enzymeName: string): number => {
    const enzyme = enzymeLibrary.find(e => e.name === enzymeName);
    return enzyme ? enzyme.cutPosition : 1;
  };

  // 根据切点数量自适应圆环半径（切点多 -> 半径变小，为外层标签留空间）
  const computeAdaptiveRadius = (nSites: number) => {
    // 220 -> 120 动态缩放；每增加5个切点，半径减少6，最小120
    const r = 220 - Math.min(100, Math.floor(nSites / 5) * 6);
    return Math.max(120, Math.min(220, r));
  };

  // 强化的无重叠布局算法（椭圆环分布 + 短引导线，不穿圈）
  const calculateImprovedLayout = () => {
    const radius = computeAdaptiveRadius(cutSites.length);
    const centerX = 350; // SVG宽度700的中心
    const centerY = 375; // SVG高度750的中心
    const anglePerBase = (2 * Math.PI) / seqLength;

    // 常量参数（可微调）
    const LABEL_FONT = '10px Arial';
    const PAD = 8;                     // 文本左右留白
    const STUB = 10;                   // 径向短线长度
    const BELT_MARGIN = 10;            // 标签带与环的额外间距
    const BASE_BELT = radius + STUB + BELT_MARGIN; // 基础标签带（用于碰撞估算）
    const LAYER_STEP = 18;             // 多层半径步进（略大，减少拥挤）
    const MIN_GAP_ANGLE = 0.03;        // 相邻标签最小角距（约1.7°）
    const MAX_LAYERS = 4;
    const TANGENT_LEN_BASE = 8;        // 切向短线基础长度（更短）
    const SHIFT_CAP = 0.18;            // 单标签角度偏移上限（~10°），避免远离切点

    const measureText = (text: string) => {
      try {
        if (!measureCanvasRef.current) {
          measureCanvasRef.current = document.createElement('canvas');
        }
        const ctx = measureCanvasRef.current.getContext('2d');
        if (!ctx) return Math.max(40, text.length * 6.4);
        ctx.font = LABEL_FONT;
        const m = ctx.measureText(text);
        return Math.max(40, m.width);
      } catch {
        return Math.max(40, text.length * 6.4);
      }
    };

    const norm = (a: number) => {
      let x = a;
      while (x < 0) x += 2 * Math.PI;
      while (x >= 2 * Math.PI) x -= 2 * Math.PI;
      return x;
    };

    type Item = {
      enzyme: string;
      site: CutSite;
      siteIndex: number;
      cutAngle: number;
      cutX: number;
      cutY: number;
      color: string;
      text: string;
      width: number; // 像素
    };

    const items: Item[] = [];
    Object.entries(enzymeGroups).forEach(([enzyme, sites]) => {
      const count = sites.length || 1;
      sites.forEach((site, siteIndex) => {
        const pos0 = site.cutPosition - 1;
        const ang = pos0 * anglePerBase - Math.PI / 2;
        const cutX = centerX + radius * Math.cos(ang);
        const cutY = centerY + radius * Math.sin(ang);
        const right = ang > -Math.PI / 2 && ang < Math.PI / 2;
        const text = right ? `${enzyme}[${count}](${site.cutPosition})` : `(${site.cutPosition})[${count}]${enzyme}`;
        const width = measureText(text) + PAD;
        items.push({ enzyme, site, siteIndex, cutAngle: ang, cutX, cutY, color: colorMap[enzyme], text, width });
      });
    });

    items.sort((a, b) => norm(a.cutAngle) - norm(b.cutAngle));

    // 角度邻近聚类（折叠）：将极度密集的相邻位点合并显示
    const CLUSTER_WINDOW = 0.05; // ≈ 2.9°
    type Unit = (Item & { isCluster?: false }) | ({
      isCluster: true;
      cutAngle: number;
      cutX: number;
      cutY: number;
      text: string; // 显示为 "X 等(n)"
      color: string;
      members: Item[];
    });
    const units: Unit[] = [];
    let group: Item[] = [];
    const flushGroup = () => {
      if (group.length === 0) return;
      if (group.length >= 3) {
        const avgAngle = group.reduce((s, it) => s + it.cutAngle, 0) / group.length;
        const cutAngle = avgAngle;
        const cutX = centerX + radius * Math.cos(cutAngle);
        const cutY = centerY + radius * Math.sin(cutAngle);
        const text = `${group[0].enzyme} 等(${group.length})`;
        units.push({ isCluster: true, cutAngle, cutX, cutY, text, color: '#666', members: [...group] });
      } else {
        group.forEach(it => units.push(it));
      }
      group = [];
    };
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (group.length === 0) {
        group.push(it);
      } else {
        const prev = group[group.length - 1];
        const delta = Math.abs(norm(it.cutAngle) - norm(prev.cutAngle));
        if (delta <= CLUSTER_WINDOW) group.push(it); else { flushGroup(); group.push(it); }
      }
    }
    flushGroup();

    // 每层的“已占用结束角度”
    const layerEnd: number[] = Array.from({ length: MAX_LAYERS }, () => -Infinity);

    type Placement = {
      id: string;
      enzyme: string;
      site: CutSite;
      siteIndex: number;
      cutAngle: number;
      labelAngle: number;
      labelRadius: number;
      cutX: number;
      cutY: number;
      labelX: number;
      labelY: number;
      radialEndX: number;
      radialEndY: number;
      color: string;
      textConnectionX: number;
      textAnchor: string;
      // cluster support
      isCluster?: boolean;
      labelText?: string;
      members?: Item[];
    };

    const placements: Placement[] = [];

    const ellipseRadiusAt = (theta: number, baseR: number) => baseR; // 仅用于碰撞估算，不再真实用作定位

    for (const unit of units) {
      let placed = false;
      for (let layer = 0; layer < MAX_LAYERS && !placed; layer++) {
        const beltR = BASE_BELT + layer * LAYER_STEP;
        const widthPx = (unit as any).width ?? (measureText((unit as any).text || 'CLUSTER') + PAD);
        const halfAngle = (widthPx / beltR) / 2;
        const cutAngle = (unit as any).cutAngle as number;
        let labelAngle = cutAngle;
        const minStart = layerEnd[layer] + MIN_GAP_ANGLE + halfAngle;
        const proposed = Math.max(cutAngle, minStart);
        // 若本层需要的偏移超过上限，尝试下一层
        if (proposed - cutAngle > SHIFT_CAP) {
          continue;
        }
        labelAngle = proposed;

        const end = labelAngle + halfAngle;

        // 放置（文字靠近径向末端，沿切线方向偏移一个很短的距离）
        const radialEndX = centerX + (radius + STUB) * Math.cos(cutAngle);
        const radialEndY = centerY + (radius + STUB) * Math.sin(cutAngle);
        const isRight = Math.cos(cutAngle) > 0; // 右半边取决于切点
        // 第二段改为“纯径向”连接至文字锚点附近，避免跨圈与交叉
        const connX = (centerX + (BASE_BELT + layer * LAYER_STEP) * Math.cos(cutAngle)) + (isRight ? -PAD / 2 : PAD / 2);
        const connY = centerY + (BASE_BELT + layer * LAYER_STEP) * Math.sin(cutAngle);
        const textAnchor = isRight ? 'start' : 'end';
        const labelX = connX + (isRight ? PAD / 2 : -PAD / 2);
        const labelY = connY;

        const isCluster = (unit as any).isCluster === true;
        const firstMember: Item | undefined = isCluster ? (unit as any).members?.[0] : undefined;
        const enzyme = isCluster ? (firstMember?.enzyme || 'Cluster') : (unit as any).enzyme;
        const site = isCluster ? (firstMember?.site as CutSite) : (unit as any).site;
        const siteIndex = isCluster ? -1 : (unit as any).siteIndex;
        const id = isCluster ? `cluster-${layer}-${Math.round(labelAngle * 1000)}` : `${enzyme}-${siteIndex}`;
        const color = isCluster ? '#666' : (unit as any).color;

        placements.push({
          id,
          enzyme,
          site,
          siteIndex,
          cutAngle: (unit as any).cutAngle,
          labelAngle,
          labelRadius: beltR,
          cutX: (unit as any).cutX,
          cutY: (unit as any).cutY,
          labelX,
          labelY,
          radialEndX,
          radialEndY,
          color,
          textConnectionX: connX,
          textAnchor,
          isCluster,
          labelText: isCluster ? (unit as any).text : undefined,
          members: isCluster ? (unit as any).members : undefined
        });

        layerEnd[layer] = end;
        placed = true;
      }
      // 若仍未放置，后续可聚类；当前版本忽略超限项
    }

    return placements;
  };

  // 弧形文字布局：将标签贴在圆外的同心弧线上，避免“文字与连线分离”
  const calculateArcLabelLayout = () => {
    const radius = computeAdaptiveRadius(cutSites.length);
    const centerX = 350;
    const centerY = 375;
    const anglePerBase = (2 * Math.PI) / seqLength;

    const LABEL_FONT = '10px Arial';
    const PAD = 8;                 // 文本左右留白
    const BELT_MARGIN = 8;         // 标签弧与环的间隔
    const BELT_RADIUS = radius + BELT_MARGIN; // 第一层标签弧半径
    const LAYER_STEP = 18;         // 层间距
    const MIN_GAP = 0.03;          // 相邻弧之间的最小角距（约1.7°）

    const measureText = (text: string) => {
      try {
        if (!measureCanvasRef.current) {
          measureCanvasRef.current = document.createElement('canvas');
        }
        const ctx = measureCanvasRef.current.getContext('2d');
        if (!ctx) return Math.max(40, text.length * 6.4);
        ctx.font = LABEL_FONT;
        const m = ctx.measureText(text);
        return Math.max(40, m.width);
      } catch {
        return Math.max(40, text.length * 6.4);
      }
    };

    type ArcItem = {
      enzyme: string;
      site: CutSite;
      siteIndex: number;
      angle: number;     // 切点中心角
      cutX: number;
      cutY: number;
      text: string;
      color: string;
      width: number;
    };

    const items: ArcItem[] = [];
    Object.entries(enzymeGroups).forEach(([enzyme, sites]) => {
      const count = sites.length || 1;
      sites.forEach((site, siteIndex) => {
        const pos0 = site.cutPosition - 1;
        const ang = pos0 * anglePerBase - Math.PI / 2;
        const cutX = centerX + radius * Math.cos(ang);
        const cutY = centerY + radius * Math.sin(ang);
        const textRight = ang > -Math.PI / 2 && ang < Math.PI / 2;
        const text = textRight
          ? `${enzyme}[${count}](${site.cutPosition})`
          : `(${site.cutPosition})[${count}]${enzyme}`;
        const width = measureText(text) + PAD;
        items.push({ enzyme, site, siteIndex, angle: ang, cutX, cutY, text, color: colorMap[enzyme], width });
      });
    });

    const normalize = (a: number) => {
      let x = a;
      while (x < 0) x += 2 * Math.PI;
      while (x >= 2 * Math.PI) x -= 2 * Math.PI;
      return x;
    };
    items.sort((a, b) => normalize(a.angle) - normalize(b.angle));

    const layersLastEnd: number[] = []; // 每层已经占用到的角度（以弧度计，基于0..2π环）

    type ArcPlacement = {
      enzyme: string;
      site: CutSite;
      siteIndex: number;
      cutX: number;
      cutY: number;
      color: string;
      pathId: string;
      d: string;
      text: string;
    };

    const arcPlacements: ArcPlacement[] = [];

    const polarToCartesian = (cx: number, cy: number, r: number, angle: number) => ({
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle)
    });

    const describeSmallArc = (cx: number, cy: number, r: number, startAngle: number, endAngle: number) => {
      const start = polarToCartesian(cx, cy, r, startAngle);
      const end = polarToCartesian(cx, cy, r, endAngle);
      const largeArcFlag = Math.abs(endAngle - startAngle) <= Math.PI ? '0' : '1';
      const sweepFlag = '1';
      return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`;
    };

    items.forEach((it, idx) => {
      // 尝试放到最内层开始的某一层
      let placed = false;
      for (let layer = 0; layer < 3 && !placed; layer++) {
        const r = BELT_RADIUS + layer * LAYER_STEP;
        const theta = (it.width / r);
        let start = it.angle - theta / 2;
        let end = it.angle + theta / 2;

        // 与该层上一个占位比较（简单环上比较：允许少量误差）
        const lastEnd = layersLastEnd[layer] ?? -Infinity;
        const startN = normalize(start);
        const endN = normalize(end);

        // 简化处理：不精确考虑跨0边界的重叠，已足够稳定
        if (!isFinite(lastEnd) || (startN > lastEnd + MIN_GAP)) {
          // 方向：下半区文字反向，以保持不倒置
          const isBottom = it.angle > Math.PI / 2 && it.angle < 3 * Math.PI / 2;
          let d: string;
          if (isBottom) {
            // 反向画弧（从 end 到 start）
            d = describeSmallArc(centerX, centerY, r, end, start);
          } else {
            d = describeSmallArc(centerX, centerY, r, start, end);
          }

          arcPlacements.push({
            enzyme: it.enzyme,
            site: it.site,
            siteIndex: it.siteIndex,
            cutX: it.cutX,
            cutY: it.cutY,
            color: it.color,
            pathId: `arc-label-${idx}-${layer}`,
            d,
            text: it.text
          });
          layersLastEnd[layer] = endN;
          placed = true;
        }
      }
      // 若三层都没放下，忽略或后续可聚类（下一步可扩展）
    });

    return arcPlacements;
  };

  // 处理酶切位点点击
  const handleCutSiteClick = (site: CutSite) => {
    setHighlightedSite(site);
    setSelectedEnzymeForHighlight(site.enzymeName);
    
    // 滚动序列到对应位置
    scrollSequenceToPosition(site.cutPosition);
  };

  // 处理酶标签点击 - 循环显示多个位点
  const handleEnzymeClick = (enzymeName: string) => {
    setSelectedEnzyme(enzymeName);
    setSelectedEnzymeForHighlight(enzymeName);
    
    const sites = enzymeGroups[enzymeName] || [];
    if (sites.length === 0) return;
    
    // 计算应该显示的位点索引
    const currentCount = clickCount[enzymeName] || 0;
    const nextIndex = currentCount % sites.length;
    
    // 更新点击计数
    setClickCount({ ...clickCount, [enzymeName]: currentCount + 1 });
    
    // 获取当前位点并高亮
    const site = sites[nextIndex];
    setHighlightedSite(site);
    
    // 滚动到对应序列位置
    scrollSequenceToPosition(site.cutPosition);
  };

  // 滚动序列到指定位置
  const scrollSequenceToPosition = (position: number) => {
    const sequenceContainer = document.querySelector('.sequence-display-container');
    if (sequenceContainer) {
      const basesPerLine = 50;
      const targetLine = Math.floor((position - 1) / basesPerLine);
      const lineHeight = 24;
      const scrollPosition = targetLine * lineHeight;
      
      sequenceContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // 处理聚类标签点击：在成员中循环高亮
  const handleClusterClick = (placement: { id: string, members: any[] }) => {
    if (!placement?.members || placement.members.length === 0) return;
    const key = placement.id;
    const idx = (clusterClickCount[key] || 0) % placement.members.length;
    const member = placement.members[idx];
    setClusterClickCount({ ...clusterClickCount, [key]: (clusterClickCount[key] || 0) + 1 });
    setSelectedEnzymeForHighlight(member.enzyme);
    setHighlightedSite(member.site);
    scrollSequenceToPosition(member.site.cutPosition);
  };

  // 自适应序列列宽
  const sequenceContainerRef = useRef<HTMLDivElement | null>(null);
  const [basesPerLine, setBasesPerLine] = useState<number>(50);
  useEffect(() => {
    const measure = () => {
      const el = sequenceContainerRef.current;
      if (!el) return;
      // 预留行号和左右内边距
      const available = el.clientWidth - 50 - 32;
      // 计算等宽字体字符宽度
      let charW = 8;
      try {
        if (!measureCanvasRef.current) measureCanvasRef.current = document.createElement('canvas');
        const ctx = measureCanvasRef.current.getContext('2d');
        if (ctx) {
          ctx.font = '12px Monaco, Consolas, monospace';
          charW = Math.max(7, Math.ceil(ctx.measureText('A').width));
        }
      } catch {}
      const perLine = Math.max(30, Math.floor(available / charW));
      setBasesPerLine(perLine);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // 渲染多行序列显示
  const renderSequenceDisplay = () => {
    const lines: { start: number; sequence: string; startIndex: number }[] = [];
    
    for (let i = 0; i < seqLength; i += basesPerLine) {
      const lineSequence = cleanSequence.slice(i, Math.min(i + basesPerLine, seqLength));
      const lineStart = i + 1;
      
      lines.push({
        start: lineStart,
        sequence: lineSequence,
        startIndex: i
      });
    }

    return (
      <div 
        className="sequence-display-container"
        ref={sequenceContainerRef}
        style={{ 
          fontFamily: 'Monaco, Consolas, monospace', 
          fontSize: '12px',
          lineHeight: '1.8',
          backgroundColor: '#f8f9fa',
          padding: '16px',
          borderRadius: '4px',
          height: '100%',
          overflowY: 'auto',
          width: '100%'
        }}>
        {lines.map((line, lineIndex) => {
          return (
            <div key={lineIndex} style={{ marginBottom: '4px', display: 'flex' }}>
              {/* 行号 */}
              <span style={{ 
                color: '#666', 
                marginRight: '16px', 
                minWidth: '50px',
                textAlign: 'right'
              }}>
                {line.start}
              </span>
              
              {/* 序列 */}
              <span>
                {line.sequence.split('').map((base, baseIndex) => {
                  const globalPos = line.startIndex + baseIndex + 1;
                  let isHighlighted = false;
                  let isCutSite = false;
                  let highlightColor = '#ffeb3b';
                  
                  // 检查是否在高亮的识别序列中
                  if (highlightedSite) {
                    const enzyme = enzymeLibrary.find(e => e.name === highlightedSite.enzymeName);
                    if (enzyme) {
                      const recognitionLength = enzyme.recognitionSite.length;
                      // 修正识别序列起始位置的计算
                      // highlightedSite.cutPosition 是实际的切割位置（1-based）
                      // enzyme.cutPosition 是相对于识别序列起点的切割位置（0-based）
                      const siteStart = highlightedSite.cutPosition - enzyme.cutPosition + 1;
                      const siteEnd = siteStart + recognitionLength;
                      
                      if (globalPos >= siteStart && globalPos < siteEnd) {
                        isHighlighted = true;
                        highlightColor = colorMap[highlightedSite.enzymeName] + '40';
                      }
                      
                      // 根据cutOffset确定切割线位置 (0: 前, 1: 后)
                      const cutOffset = enzyme.cutOffset ?? 1; // 默认为1，即切在碱基之后
                      // 正确计算：识别序列起始位置 + 酶内部切割位置 + 偏移量
                      const visualCutPosition = siteStart + enzyme.cutPosition + cutOffset;
                      
                      if (globalPos === visualCutPosition) {
                        isCutSite = true;
                      }
                    }
                  }
                  
                  return (
                    <span
                      key={baseIndex}
                      style={{
                        backgroundColor: isHighlighted ? highlightColor : 'transparent',
                        borderLeft: isCutSite ? `2px solid ${colorMap[highlightedSite?.enzymeName || '']}` : 'none',
                        paddingLeft: isCutSite ? '2px' : '0',
                        fontWeight: isHighlighted ? 'bold' : 'normal'
                      }}
                    >
                      {base}
                    </span>
                  );
                })}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // 改进的环形视图渲染函数
  const renderCircularView = () => {
    const radius = computeAdaptiveRadius(cutSites.length);
    const centerX = 350; // SVG宽度700的中心
    const centerY = 375; // SVG高度750的中心
    const anglePerBase = (2 * Math.PI) / seqLength;
    const gapAngle = 0.2;
    const startArc = -Math.PI / 2 + gapAngle / 2;
    const endArc = 3 * Math.PI / 2 - gapAngle / 2;
    const polarToCartesian = (cx: number, cy: number, r: number, angle: number) => ({
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle)
    });
    const describeArc = (cx: number, cy: number, r: number, startAngle: number, endAngle: number) => {
      const start = polarToCartesian(cx, cy, r, startAngle);
      const end = polarToCartesian(cx, cy, r, endAngle);
      const largeArcFlag = (endAngle - startAngle) <= Math.PI ? '0' : '1';
      return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
    };
    const angleInGap = (angle: number) => {
      const delta = Math.atan2(Math.sin(angle + Math.PI / 2), Math.cos(angle + Math.PI / 2));
      return Math.abs(delta) <= gapAngle / 2;
    };
    
    const labelPositions = calculateImprovedLayout();

    return (
      <div style={{ display: 'flex', gap: '20px', alignItems: 'stretch', justifyContent: 'flex-start', width: '100%' }}>
        {/* 左侧：环形视图 - 扩大SVG容器以容纳所有标签 */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <svg 
            ref={svgRef} 
            width="700" 
            height="750"
            viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp} // 如果鼠标移出SVG，也停止平移
            style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
          >
            {/* 环形序列路径（带缺口） */}
            <path
              d={describeArc(centerX, centerY, radius, startArc, endArc)}
              fill="none"
              stroke="#cfcfcf"
              strokeWidth="2.5"
            />

            {/* 序列刻度标记（降低密度：10为小刻度，100为大刻度） */}
            {Array.from({ length: Math.floor(seqLength / 10) + 1 }, (_, k) => k * 10).map((i) => {
              const angle = i * anglePerBase - Math.PI / 2;
              if (angleInGap(angle)) return null;
              const x = centerX + radius * Math.cos(angle);
              const y = centerY + radius * Math.sin(angle);
              const isMajor = i % 100 === 0;
              const tickLength = isMajor ? 10 : 5;
              const tickX = centerX + (radius + tickLength) * Math.cos(angle);
              const tickY = centerY + (radius + tickLength) * Math.sin(angle);
              return (
                <g key={`tick-${i}`}>
                  <line
                    x1={x}
                    y1={y}
                    x2={tickX}
                    y2={tickY}
                    stroke={isMajor ? '#9aa0a6' : '#bdbdbd'}
                    strokeWidth={isMajor ? '1.3' : '0.8'}
                    strokeLinecap="round"
                  />
                </g>
              );
            })}

            {/* 酶切位点标记与外侧标签（带短引导线） */}
            {labelPositions.map((labelInfo: any) => {
              const isHighlighted = highlightedSite?.enzymeName === labelInfo.enzyme && 
                                   highlightedSite?.cutPosition === labelInfo.site.cutPosition;
              const isEnzymeSelected = selectedEnzymeForHighlight === labelInfo.enzyme;
              const enzymeCount = enzymeGroups[labelInfo.enzyme]?.length || 1;
              const isCluster = !!labelInfo.isCluster;
              const labelText = isCluster ? (labelInfo.labelText || '') : (labelInfo.textAnchor === 'start' 
                ? `${labelInfo.enzyme}[${enzymeCount}](${labelInfo.site?.cutPosition})`
                : `(${labelInfo.site?.cutPosition})[${enzymeCount}]${labelInfo.enzyme}`);
              const tooltipText = isCluster && Array.isArray(labelInfo.members)
                ? labelInfo.members.map((m: any) => `${m.enzyme}(${m.site.cutPosition})`).join(' | ')
                : labelText;
              return (
                <g key={labelInfo.id || `${labelInfo.enzyme}-${labelInfo.siteIndex}`}>
                  {/* 径向短线 */}
                  <line
                    x1={labelInfo.cutX}
                    y1={labelInfo.cutY}
                    x2={labelInfo.radialEndX}
                    y2={labelInfo.radialEndY}
                    stroke={isEnzymeSelected ? '#ff0000' : '#666'}
                    strokeWidth={isEnzymeSelected ? '1.5' : '1'}
                    opacity={0.85}
                    strokeLinecap="round"
                  />
                  {/* 切向短线到文本锚点（直接连到文字 x） */}
                  <line
                    x1={labelInfo.radialEndX}
                    y1={labelInfo.radialEndY}
                    x2={labelInfo.textConnectionX}
                    y2={labelInfo.labelY}
                    stroke={isEnzymeSelected ? '#ff0000' : '#666'}
                    strokeWidth={isEnzymeSelected ? '1.5' : '1'}
                    opacity={0.85}
                    strokeLinecap="round"
                  />
                  {/* 切点圆点 */}
                  <circle
                    cx={labelInfo.cutX}
                    cy={labelInfo.cutY}
                    r={isHighlighted || isEnzymeSelected ? '2.5' : '1.5'}
                    fill={isEnzymeSelected ? '#ff0000' : labelInfo.color}
                    stroke="white"
                    strokeWidth="1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => isCluster ? handleClusterClick(labelInfo) : handleCutSiteClick(labelInfo.site)}
                  />
                  {/* 文本（水平，左侧对齐为 end，右侧为 start） */}
                  <text
                    x={labelInfo.labelX}
                    y={labelInfo.labelY}
                    textAnchor={labelInfo.textAnchor as any}
                    dominantBaseline="middle"
                    fontSize="10"
                    fill={isEnzymeSelected ? '#ff0000' : '#2c2c2c'}
                    fontWeight={isEnzymeSelected ? 'bold' : 'normal'}
                    fontFamily="Arial, sans-serif"
                    style={{ cursor: 'pointer' }}
                    onClick={() => isCluster ? handleClusterClick(labelInfo) : handleEnzymeClick(labelInfo.enzyme)}
                  >
                    <title>{tooltipText}</title>
                    {labelText}
                  </text>
                </g>
              );
            })}

            {/* 中心标题 */}
            <text
              x={centerX}
              y={centerY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="14"
              fontWeight="bold"
              fill="#333"
            >
              {seqLength} bp
            </text>
          </svg>
        </div>
        
        {/* 右侧：序列显示 - 默认较窄，但可根据可用空间自适应扩展，避免右侧空白 */}
        <div style={{ flex: '1 1 420px', minWidth: '280px', height: '750px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: '12px', flexShrink: 0 }}>
            <Text strong>序列显示</Text>
            {highlightedSite && (
              <div style={{ marginTop: '8px' }}>
                <Text type="secondary">
                  已选择: <span style={{ color: colorMap[highlightedSite.enzymeName], fontWeight: 'bold' }}>
                    {highlightedSite.enzymeName}
                  </span> 在位置 {highlightedSite.cutPosition}
                </Text>
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: 'hidden', width: '100%' }}>
            {renderSequenceDisplay()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: '100%' }}>
      {renderCircularView()}
    </div>
  );
};

export default CutMap;